import { useState } from 'react'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '../../form'
import { BankSelector } from '@/modules/bank/components/BankSelector'
import { useForm } from 'react-hook-form'
import { WithdrawTypeSelectionModal } from './WithdrawTypeSelectionModal'

const schema = z.object({
	amount: z
		.union([
			z.string().min(1, 'กรุณากรอกจำนวนเงิน').transform(Number),
			z.number(),
		])
		.refine((val) => typeof val === 'number' && !isNaN(val) && val > 0, {
			message: 'จำนวนเงินต้องมากกว่า 0',
		}),
	phoneNumber: z
		.string()
		.min(1, 'กรุณากรอกเบอร์โทรศัพท์')
		.regex(/^\d{9,15}$/, 'เบอร์โทรศัพท์ไม่ถูกต้อง'),
	bankProviderName: z.string().optional(),
	accountNumber: z.string().optional(),
	accountName: z.string().optional(),
	note: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

type WithdrawData = {
	amount: number
	playerPhone: string
	bankProviderName?: string
	accountNumber?: string
	accountName?: string
	note?: string
}

export function CreateManualWithdrawDialog() {
	const [open, setOpen] = useState(false)
	const [typeModalOpen, setTypeModalOpen] = useState(false)
	const [withdrawData, setWithdrawData] = useState<WithdrawData | null>(null)

	const form = useForm<FormValues>({
		defaultValues: {
			phoneNumber: '',
			bankProviderName: '',
			accountNumber: '',
			accountName: '',
			note: '',
		},
		resolver: zodResolver(schema),
	})

	const onSubmit = async (values: FormValues) => {
		// Prepare data and open type selection modal
		const data: WithdrawData = {
			amount: Number(values.amount),
			playerPhone: String(values.phoneNumber),
			bankProviderName: values.bankProviderName
				? String(values.bankProviderName)
				: undefined,
			accountNumber: values.accountNumber
				? String(values.accountNumber)
				: undefined,
			accountName: values.accountName ? String(values.accountName) : undefined,
			note: values.note ? String(values.note) : undefined,
		}

		setWithdrawData(data)
		setTypeModalOpen(true)
	}

	const handleSuccess = () => {
		setOpen(false)
		form.reset()
		setWithdrawData(null)
	}
	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button>สร้างรายการถอนเงิน</Button>
			</DialogTrigger>
			<DialogContent className="flex max-h-[90vh] flex-col gap-0 p-0">
				<DialogHeader className="shrink-0 px-6 pb-4 pt-6">
					<DialogTitle>สร้างรายการถอนเงิน (Manual)</DialogTitle>
				</DialogHeader>
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="space-y-4 overflow-y-auto px-6 pb-6"
					>
						<FormField
							control={form.control}
							name="phoneNumber"
							render={({ field }) => (
								<FormItem>
									<FormLabel>เบอร์โทรศัพท์</FormLabel>
									<FormControl>
										<Input {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="amount"
							render={({ field }) => (
								<FormItem>
									<FormLabel>จำนวนเงิน</FormLabel>
									<FormControl>
										<Input
											type="number"
											{...field}
											value={field.value}
											onChange={(e) => field.onChange(e.target.value)}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<BankSelector
							control={form.control}
							name="bankProviderName"
							required={false}
						/>
						<FormField
							control={form.control}
							name="accountNumber"
							render={({ field }) => (
								<FormItem>
									<FormLabel>หมายเลขบัญชี (ถ้ามี)</FormLabel>
									<FormControl>
										<Input {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="accountName"
							render={({ field }) => (
								<FormItem>
									<FormLabel>ชื่อบัญชี (ถ้ามี)</FormLabel>
									<FormControl>
										<Input {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="note"
							render={({ field }) => (
								<FormItem>
									<FormLabel>หมายเหตุ (ถ้ามี)</FormLabel>
									<FormControl>
										<Textarea
											{...field}
											placeholder="ระบุเหตุผลหรือหมายเหตุเพิ่มเติม..."
											className="min-h-[80px]"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<Button type="submit" className="w-full">
							ยืนยัน
						</Button>
					</form>
				</Form>
			</DialogContent>
			<WithdrawTypeSelectionModal
				open={typeModalOpen}
				onOpenChange={setTypeModalOpen}
				withdrawData={withdrawData}
				onSuccess={handleSuccess}
			/>
		</Dialog>
	)
}
